import type { WaveJudgement } from '../judge/Judgement.js';
import { WaveState } from './WaveState.js';

/**
 * One wave, as a plain record.
 *
 * Deliberately not a scene object. In the Unity build a wave *was* a
 * `GameObject` carrying a `MonoBehaviour`, which is why its logic and its
 * appearance could not be separated and why the collision code ended up reading
 * `Time.deltaTime` and `Camera.main`. Here the simulation owns records and the
 * view reads them; nothing in this file knows that a renderer exists.
 *
 * Records are pooled and reused, so the colour buffers are allocated once at
 * pool construction and overwritten on spawn. A frame that spawns eight waves
 * should allocate nothing.
 */
export interface Wave {
  /** Stable identity across the pool, so views can track a wave between frames. */
  readonly id: number;
  /** Slot is in use. Retired waves stay in the array until reused. */
  active: boolean;
  state: WaveState;

  /** Index into the compiled chart's source-order arrays. */
  eventIndex: number;
  /** `event.time`: when the scheduler created this wave. */
  spawnTime: number;
  /** `event.time + warningLeadTime`: when the radius starts growing. */
  expansionStartTime: number;
  /**
   * Song time this wave most wants to be tapped at. See `judge/BestTime.ts`.
   *
   * Purely a target for grading and for the timing arc. Nothing about where the
   * wave is, how fast it grows or when it expires reads this field.
   */
  bestTime: number;

  /**
   * When the wave becomes visible, and when it starts growing.
   *
   * These are the **only** timings the wave speed setting touches. They are
   * derived backwards from `bestTime`, which is why turning the speed up makes
   * a wave appear later and rush, and turning it down makes it appear early and
   * drift — while the moment it wants to be tapped does not move at all. That
   * separation is the whole point: a speed control that moved `bestTime` would
   * be a difficulty control wearing a disguise.
   */
  visualSpawnTime: number;
  visualExpansionStart: number;
  /**
   * `bestTime - visualSpawnTime`. Carried on the record rather than recomputed
   * because the renderer needs it every frame to place the converging note, and
   * a second derivation is a second thing that can disagree.
   */
  visualLeadTime: number;
  /** `event.speed` times the user's multiplier. World units per second. */
  visualSpeed: number;

  originX: number;
  originY: number;

  initialRadius: number;
  speed: number;
  /** Current radius, always recomputed from song time rather than accumulated. */
  radius: number;
  /** Seconds since `spawnTime`. */
  age: number;
  /** Radius at which Unity releases the wave: `despawnMargin + |origin|`. */
  despawnRadius: number;

  intensity: number;
  gateAngle: number;
  gateCenterAngle: number;
  gateWidth: number;
  damage: number;
  baseScore: number;

  /**
   * Set once the wave has been graded, whether by a tap or by expiring.
   *
   * Also the guard against a second resolution: a wave with a judgement is out
   * of the hit test, so two taps in one frame — or a tap on a wave that expired
   * the same frame — cannot both score.
   */
  judgement: WaveJudgement | null;
  /** Song time the judgement landed. Drives the dissolve, and nothing else. */
  resolvedAt: number;
  /** Dissolve length for the grade this wave got. */
  fadeSeconds: number;

  /** Failed taps this wave has taken. A wave can collect any number. */
  badAttempts: number;
  /** Song time of the most recent failed tap; drives the glitch. */
  lastBadAt: number;
  /** Refuses further input until this song time, after a failed tap. */
  lockedUntil: number;

  /** RGBA, authored values carried straight from the chart. */
  readonly dangerColor: Float32Array;
  readonly gateColor: Float32Array;
  readonly paintColor: Float32Array;
}

export function createWave(id: number): Wave {
  return {
    id,
    active: false,
    state: WaveState.Idle,
    eventIndex: -1,
    spawnTime: 0,
    expansionStartTime: 0,
    bestTime: 0,
    visualSpawnTime: 0,
    visualExpansionStart: 0,
    visualLeadTime: 0,
    visualSpeed: 0,
    originX: 0,
    originY: 0,
    initialRadius: 0,
    speed: 0,
    radius: 0,
    age: 0,
    despawnRadius: 0,
    intensity: 0,
    gateAngle: 0,
    gateCenterAngle: 0,
    gateWidth: 0,
    damage: 0,
    baseScore: 0,
    judgement: null,
    resolvedAt: 0,
    fadeSeconds: 0,
    badAttempts: 0,
    lastBadAt: -1e9,
    lockedUntil: -1e9,
    dangerColor: new Float32Array(4),
    gateColor: new Float32Array(4),
    paintColor: new Float32Array(4),
  };
}

export function resetWave(wave: Wave): void {
  wave.active = false;
  wave.state = WaveState.Idle;
  wave.eventIndex = -1;
  wave.radius = 0;
  wave.age = 0;
  wave.judgement = null;
  wave.resolvedAt = 0;
  wave.fadeSeconds = 0;
  wave.badAttempts = 0;
  wave.lastBadAt = -1e9;
  wave.lockedUntil = -1e9;
}
